import {Injectable} from '@angular/core';
import {ViewConfig} from '../../model/api/view-config';
import {Trade} from '../../model/clearing/trade';
import {AccountDefinition} from '../../model/customer/account-definition';
import {FieldRecord} from '../../model/api/field-record';
import {ExchangeDetail} from '../../model/exchange/exchange-detail';
import {DatePipe, DecimalPipe} from "@angular/common";
import {OrdersTradesFilter} from "../../model/clearing/orders-trades-filter";
import {TableColumnConfig} from "../../model/api/table-column-config";
import {TableRequest} from "../../model/api/table-request";
import CallPut = Trade.CallPut;
import StateFilter = OrdersTradesFilter.StateFilter;

@Injectable({providedIn: 'root'})
export class ClearingTableConfigService {
  static readonly unfilterableSortableColumns = ['_matching', '_clearing', '_checked', '_aps'];

  defaultSortField: string = '';
  defaultSortOrder: number = 1;

  constructor(private decimalPipe: DecimalPipe, private datePipe: DatePipe) {
  }

  buildTableConfig(viewConfig: ViewConfig, visibleColumns: string[], exchanges: FieldRecord<ExchangeDetail>[], clearingStateColorMap: any): any[] {
    const columnsConfig: any[] = [];

    viewConfig.templateRows.forEach(value => {
      const columnConfig = value.viewColumn;
      const field = columnConfig.field?.toLowerCase();

      // Header styles
      columnConfig.headerStyle = this.getHeaderStyle(value);

      // Cell styles
      if (value.contentColorFormat) {
        columnConfig.style = this.getStyleCallback(value.contentColorFormat, clearingStateColorMap);
      }

      // Column-specific behavior
      this.applySpecialColumnBehavior(columnConfig, field, exchanges);

      // Filtering
      if (!ClearingTableConfigService.unfilterableSortableColumns.includes(field.toLowerCase())) {
        this.applyFilterConfig(columnConfig, field);
      } else {
        columnConfig.notFilterable = true;
        columnConfig.notSortable = true;
      }

      // Visibility
      if (!visibleColumns?.length && value.disabled) {
        columnConfig.hidden = value.disabled;
      } else if (visibleColumns?.length) {
        columnConfig.hidden = !visibleColumns.includes(columnConfig.field);
      }

      // Add if visible
      if (!columnConfig.hidden) columnsConfig.push(columnConfig);
    });

    // Inject always-hidden system columns
    this.addRequiredHiddenColumns(columnsConfig);

    // Sort if applicable
    if (visibleColumns?.length) {
      columnsConfig.sort((a, b) => visibleColumns.indexOf(a.field) - visibleColumns.indexOf(b.field));
    }

    return columnsConfig;
  }

  private getHeaderStyle(value: any): string {
    let style = '';
    if (value.headerBackColor) {
      style += `background: var(--${value.headerBackColor.toLowerCase()}) !important`;
    }
    if (style && value.headerForeColor) style += ';';
    if (value.headerForeColor) {
      style += `color: var(--${value.headerForeColor.toLowerCase()}) !important`;
    }
    return style;
  }

  private getStyleCallback(format: string, colorMap: any): (record: any) => string {
    return (record: any) => {
      let style = '';
      if (format.startsWith('*:')) {
        const color = format.split(':')[1];
        style += `color:var(--${color.toLowerCase()}) !important;`;
      } else if (format === 'side_ColorCode') {
        const color = record.side === Trade.Side.BUY ? 'var(--yellowgreen)' : 'var(--tomato)';
        style += `color:${color};`;
      } else if (format === '_state_ColorCode') {
        let state = record.clearings?.[0]?.state || record.clearingInfo?.displayState || record.state;
        state = state?.replace('_', ' ') || '';
        if (colorMap[state]) {
          style += `color: var(--${colorMap[state].toLowerCase()}) !important;`;
        }
      }
      return style;
    };
  }

  private applySpecialColumnBehavior(columnConfig: any, field: string, exchanges: FieldRecord<ExchangeDetail>[]) {
    switch (field) {
      case 'displaystate':
        columnConfig.mapId = (record: any) => {
          let s = record.clearings?.[0]?.state || record.clearingInfo?.displayState || record.state;
          return s?.replace('_', ' ') || '';
        };
        columnConfig.showToolTip = true;
        break;
      case '_matching':
        columnConfig.width = 25;
        columnConfig.mapId = (r: any) => r.unmatchedVolume === 0
          ? '<span class="full-square orange"></span>'
          : r.unmatchedVolume === r.volume
            ? '<span class="empty-square orange"></span>'
            : '<span class="triangle orange"></span>';
        break;
      case '_clearing':
        columnConfig.width = 25;
        columnConfig.mapId = (r: any) => this.getClearingSymbol(r);
        break;
      case '_aps':
        columnConfig.width = 25;
        columnConfig.mapId = (r: any) => {
          if (r.avgPriceGroupId?.length > 0 && r.avgPrice > 0 && r.clearingInfo &&
            (r.clearingInfo.clearedVolume > 0 || r.clearingInfo.pendingGiveupVolume > 0 || r.clearingInfo.giveupAcceptedVolume > 0)) {
            return '<span class="full-square yellow"></span>';
          } else if (r.avgPriceGroupId && r.avgPrice > 0) {
            return '<span class="empty-square Yellow"></span>';
          } else {
            return '';
          }
        };
        break;
      case '_checked':
        columnConfig.width = 25;
        columnConfig.mapId = (r: any) => r.isChecked ? '<span class="full-square deepskyblue"></span>' : '';
        break;
      case 'side':
        columnConfig.class = (r: any) => r.side === Trade.Side.BUY ? 'YellowGreen' : 'Tomato';
        break;
      case 'account':
        columnConfig.mapId = (r: any) => r.account?.name || '';
        break;
      case 'accounttype':
        columnConfig.mapId = (r: any) => r.account?.type || '';
        break;
      case 'callput':
        columnConfig.mapId = (r: any) => r.callPut && r.callPut !== CallPut.NONE ? r.callPut : '';
        break;
      case 'accountreference1':
      case 'accountreference2':
      case 'accountreference3':
        columnConfig.mapId = (r: any) => r.account?.[field] || '';
        break;
      case 'strategy':
        columnConfig.showToolTip = true;
        break;
      case 'exchangecode':
        columnConfig.icon = (r: any) => {
          const exch = exchanges.find(e =>
            e.value?.clearingHouse === r.clearingHouse &&
            e.value?.code === r.exchangeCode
          );
          return exch?.value?.country ? `flag-icon flag-icon-${exch.value.country.toLowerCase()}` : '';
        };
        columnConfig.mapId = (r: any) => r.exchangeCode;
        break;
      case 'matching':
        columnConfig.icon = (r: any) => {
          if (r.UnMatchedVolume === 0) return 'square';
          else if (r.UnMatchedVolume === r.Volume) return '';
          else return 'triangle';
        };
        columnConfig.mapId = () => '';
        break;
    }
  }

  private getClearingSymbol(r: any): string {
    const ci = r.clearingInfo;
    if (!ci) return '';
    if (ci.giveupAcceptedVolume === r.volume) return '<span class="full-square steelblue"></span>';
    if (ci.pendingGiveupVolume === r.volume) return '<span class="empty-square lightsteelblue"></span>';
    if (ci.clearedVolume === 0 && ci.pendingGiveupVolume === 0) return '<span class="empty-square palegreen"></span>';
    if (ci.pendingGiveupVolume > 0 && ci.pendingGiveupVolume < r.volume) return '<span class="triangle lightsteelblue"></span>';
    if (ci.clearedVolume === r.volume) return '<span class="full-square palegreen"></span>';
    if (ci.clearedVolume > 0 && ci.clearedVolume < r.volume) return '<span class="triangle palegreen"></span>';
    return '';
  }

  private applyFilterConfig(columnConfig: any, field: string) {
    const filterType = ['businessdate', 'clearingdate', 'creationdate', 'executiondate'].includes(field) ? 'date' :
      ['volume', 'price', 'avgprice', 'avgrndprice', 'avgpriceresidual', 'overrideavgprice', 'strike', 'strategymissing'].includes(field) ? 'numeric' :
        ['side', 'openclose'].includes(field) ? undefined : 'text';

    columnConfig.filterConfig = {
      type: filterType,
      matchMode: filterType === 'text' ? 'contains' : undefined,
      values: undefined
    };

    if (field === 'side') {
      columnConfig.filterConfig.values = Object.values(Trade.Side);
      columnConfig.filterConfig.matchMode = 'equals';
    } else if (field === 'openclose') {
      columnConfig.filterConfig.values = Object.values(AccountDefinition.OpenClose);
      columnConfig.filterConfig.matchMode = 'in';
    } else if (field === 'callput') {
      columnConfig.filterConfig.values = Object.values(CallPut);
      columnConfig.filterConfig.matchMode = 'in';
    }
  }

  private addRequiredHiddenColumns(config: any[]) {
    const required = [
      'parentId', 'clearingHouse', 'exchangeCode', 'side', 'contractCode', 'maturity',
      'price', 'volume', 'customOrderReference', 'strike', 'callPut', 'openClose',
      'owner', 'operatorId', 'trader', 'tradingEnvironment', 'contractVersion',
      'contractUnit', 'type', 'clearingDate', 'businessDate', 'displaystate',
      'unmatchedVolume', 'isDisabled'
    ];
    for (const field of required) {
      if (!config.find(c => c.field === field)) {
        config.push({field, hidden: true});
      }
    }
  }


  /**
   * Applies the tableRecordResult into the component's internal record state
   */
  applyRecordResult(
    recordsRef: any[],
    tableRecordResult: any,
    from: number,
    to: number,
    tableConfig: any[],
    tableCurrentRequest: any,
    selectedIds: string[] | Set<string>,
    recordIds?: string[],
    recordIdToIndexMap?: Map<string, number>
  ): void {
    const tmpRecords = tableRecordResult.records ? tableRecordResult.records : [];
    if (tmpRecords.length === 0) return;

    const preloadedRecords = this.preloadDisplay(tmpRecords, tableConfig, selectedIds);
    const firstFallback = tableCurrentRequest?.first ?? 0;
    const isSet = selectedIds instanceof Set;

    preloadedRecords.forEach((newRecord, indexInResult) => {
      if (!newRecord) return;

      let recordIndex = -1;
      if (recordIdToIndexMap) {
        recordIndex = recordIdToIndexMap.get(newRecord.id) ?? -1;
      } else if (recordIds) {
        recordIndex = recordIds.indexOf(newRecord.id);
      }

      // If we still don't have a valid index, fallback to the requested offset + position in result
      if (recordIndex < 0) {
        recordIndex = firstFallback + indexInResult;
      }

      // Safeguard against out-of-bounds or invalid indices
      if (recordIndex < 0 || (recordsRef.length > 0 && recordIndex >= recordsRef.length && recordsRef.length < tableRecordResult.total)) {
        // Expand array if needed (though usually handled by updateRecordsForSync)
        if (recordIndex >= recordsRef.length) {
          recordsRef.length = recordIndex + 1;
        }
      }

      const current = recordsRef[recordIndex];
      if (!current) {
        recordsRef[recordIndex] = newRecord;
      } else {
        // In-place update to preserve references
        Object.assign(current, newRecord);
      }

      // Explicitly ensure id and selected state are set to avoid sync issues
      recordsRef[recordIndex]['id'] = newRecord['id'];
      recordsRef[recordIndex]['selected'] = isSet ? (selectedIds as Set<string>).has(newRecord['id']) : (selectedIds as string[]).includes(newRecord['id']);
    });
  }


  /**
   * Preloads styling and derived values for records
   */
  preloadDisplay(records: any[], tableConfig: any[], selectedIds: string[] | Set<string>): any[] {
    if (!records) return [];

    const isSet = selectedIds instanceof Set;
    const colSpecs = tableConfig.map(c => ({
      field: c.field,
      type: c.type,
      format: c.format,
      style: c.style,
      class: c.class,
      icon: c.icon,
      iconValue: c.iconValue,
      mapId: c.mapId,
      numericFormat: c.type === 'NUMERIC' ? (c.format ?? '1.0-7') : null,
      dateFormat: (c.type === 'DATE' || c.type === 'DATETIME') ? (c.format ?? 'dd/MM/yyyy') : null
    }));

    return records.map(record => {
      const id = record['id'];
      const newRecord: any = {
        ...record,
        selected: isSet ? (selectedIds as Set<string>).has(id) : (selectedIds as string[]).includes(id)
      };

      for (let i = 0; i < colSpecs.length; i++) {
        const spec = colSpecs[i];
        const field = spec.field;
        const value = record[field];

        // Style
        newRecord[`${field}_style`] = spec.style ? `;${spec.style(record)}` : '';

        // Class
        newRecord[`${field}_class`] = spec.class ? ` ${spec.class(record)}` : '';

        // Icon
        if (spec.icon) {
          const iconValue = spec.iconValue ? spec.iconValue(record) : '';
          newRecord[`${field}_icon_class`] = `icon ${spec.icon(record)}`;
          newRecord[`${field}_icon_value`] = iconValue;
          newRecord[`${field}_icon`] = `<i class="icon ${spec.icon(record)}">${iconValue}</i>`;
        } else {
          newRecord[`${field}_icon_class`] = '';
          newRecord[`${field}_icon_value`] = '';
          newRecord[`${field}_icon`] = '';
        }

        // Value
        if (spec.mapId) {
          const val = spec.mapId(record);
          newRecord[`${field}_value`] = val;
          if (typeof val === 'string' && val.includes('<')) {
            newRecord[`${field}_is_html`] = true;
          }
        } else if (value != null) {
          if (spec.numericFormat) {
            newRecord[`${field}_value`] = this.decimalPipe.transform(value, spec.numericFormat);
          } else if (spec.dateFormat) {
            newRecord[`${field}_value`] = this.datePipe.transform(value, spec.dateFormat);
          } else {
            newRecord[`${field}_value`] = value;
          }
        } else {
          newRecord[`${field}_value`] = '';
        }

        newRecord[field] = value ?? '';
      }

      if (record['clearingInfo']) {
        newRecord['displaystate'] = record['clearingInfo'].displayState;
      }

      return newRecord;
    });
  }

  constructRequest(
    tableConfig?: TableColumnConfig[],
    currentRequest?: TableRequest | any,
    request?: TableRequest | any
  ): TableRequest | any {
    if (!request) {
      request = {
        rows: 40,
        first: 0,
        filters: [],
        filters3: [],
        stateFilter: StateFilter.ALL,
        sortOrder: this.defaultSortOrder,
        sortField: this.defaultSortField
      };
    }

    // Ensure sort
    if (!request.sortField) {
      request.sortField = this.defaultSortField;
      request.sortOrder = this.defaultSortOrder;
    }

    // Set visible fields
    if (tableConfig && tableConfig.length > 0)
      request.visibleFields = tableConfig.map(c => c.field);


    // Clean up filters
    request.filters = (request.filters || []).filter(f => f?.value);

    const globalFilterIndex = request.filters.findIndex(f => f.field === 'global');
    if (globalFilterIndex >= 0) {
      request.globalFilter = request.filters[globalFilterIndex].value;
      request.filters.splice(globalFilterIndex, 1);
    }

    // Deduplicate filters
    const seen = new Set<string>();
    request.filters = (request.filters || []).filter(f => {
      const s = JSON.stringify(f);
      if (seen.has(s)) return false;
      seen.add(s);
      return true;
    });
    const seen3 = new Set<string>();
    request.filters3 = (request.filters3 || []).filter(f => {
      const s = JSON.stringify(f);
      if (seen3.has(s)) return false;
      seen3.add(s);
      return true;
    });

    // Preserve sync metadata
    if (currentRequest?.syncMetadata) {
      request.syncMetadata = currentRequest.syncMetadata;
    }

    // Fix rows
    if (!request.rows || request.rows <= 40) {
      request.rows = 40;
    } /*else {
      request.rows++; // one extra row for scroll preloading
    }   */

    return request;
  }
}
